/**
 * WHAT A DOCUMENT SURFACE IS ALLOWED TO CLAIM.
 *
 * A file list has one state per row: it is there. A construction information
 * surface has four, and they are NOT the same claim:
 *
 *   DOCUMENT RECORD EXISTS      a row came back and this module could read it
 *   OBJECT STORAGE HOLDS A FILE the row names a stored object (`file_url`)
 *   SIGNED URL GENERATED        the backend issued a temporary `download_url`
 *   FILE FORMAT PREVIEWABLE     this browser can render that format honestly
 *
 * Each one can be true with the next one false. A drawing whose signed URL
 * could not be minted is NOT a missing drawing, and a DWG with a perfectly
 * good signed URL is NOT a drawing OMNΛS has looked at. Collapsing any pair
 * of these into "unavailable" tells a superintendent to re-upload a file that
 * is sitting safely in the bucket, or lets the UI imply the platform read a
 * CAD file it has never opened.
 *
 * `readDocumentContract` is the other half. A payload that does not have the
 * shape the backend documents is UNREADABLE — which is a third thing again,
 * distinct from "no documents" and from "document not found". A malformed row
 * is reported as malformed and never silently rendered as an empty one.
 *
 * Pure and dependency-free on purpose: every rule below is directly testable
 * without a DOM, and nothing here reaches the network or invents a field the
 * backend does not send.
 *
 * Contract source: backend/schemas/document.py::DocumentResponse and
 * backend/routes/documents.py (verified against main at 6ec706b).
 */

export const DOCUMENT_CONTRACT = 'document-1';

/* -------------------------------------------------------------------------
 * THE VOCABULARY THE BACKEND OWNS
 *
 * These lists mirror backend/routes/documents.py exactly. They are used to
 * LABEL what the backend sent, never to reject it: a category or status this
 * frontend does not recognise is still shown, under its own raw name, because
 * the backend — not this file — decides what is valid.
 * ---------------------------------------------------------------------- */

export const DOCUMENT_CATEGORIES = [
  { value: 'drawings', label: 'Drawings' },
  { value: 'specs', label: 'Specifications' },
  { value: 'submittals', label: 'Submittals' },
  { value: 'rfis', label: 'RFIs' },
  { value: 'photos', label: 'Photos' },
  { value: 'permits', label: 'Permits' },
  { value: 'contracts', label: 'Contracts' },
  { value: 'bids', label: 'Bids' },
  { value: 'procurement', label: 'Procurement' },
  { value: 'reports', label: 'Reports' },
  { value: 'closeout', label: 'Closeout' },
  { value: 'general', label: 'General' },
];

export const DOCUMENT_STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'superseded', label: 'Superseded' },
  { value: 'archived', label: 'Archived' },
];

export const DOCUMENT_REVIEW_STATUSES = [
  { value: 'not_reviewed', label: 'Not Reviewed' },
  { value: 'in_review', label: 'In Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const CATEGORY_LABELS = Object.fromEntries(
  DOCUMENT_CATEGORIES.map((c) => [c.value, c.label]),
);

/**
 * Status presentation. `mark` exists so status never depends on colour alone:
 * the label is always rendered as text, and the mark gives a second
 * non-colour channel for a quick scan down a dense rail.
 */
const STATUS_DISPLAY = {
  draft: { label: 'Draft', tone: 'neutral', mark: '○' },
  under_review: { label: 'Under Review', tone: 'attention', mark: '◐' },
  approved: { label: 'Approved', tone: 'settled', mark: '●' },
  superseded: { label: 'Superseded', tone: 'retired', mark: '⊘' },
  archived: { label: 'Archived', tone: 'retired', mark: '▣' },
};

const REVIEW_DISPLAY = {
  not_reviewed: { label: 'Not Reviewed', tone: 'neutral', mark: '○' },
  in_review: { label: 'In Review', tone: 'attention', mark: '◐' },
  approved: { label: 'Review Approved', tone: 'settled', mark: '●' },
  rejected: { label: 'Review Rejected', tone: 'against', mark: '✕' },
};

const titleiseToken = (token) =>
  String(token)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * An unrecognised status is shown under its own name rather than mapped onto
 * a state we do recognise. Guessing here would put a word in the backend's
 * mouth about a document's standing.
 */
export const statusDisplay = (status) => {
  const key = typeof status === 'string' ? status.trim() : '';
  if (!key)
    return { label: 'Not stated', tone: 'unknown', mark: '?', known: false };
  const known = STATUS_DISPLAY[key];
  if (known) return { ...known, known: true };
  return {
    label: titleiseToken(key),
    tone: 'unknown',
    mark: '?',
    known: false,
  };
};

export const reviewStatusDisplay = (reviewStatus) => {
  const key = typeof reviewStatus === 'string' ? reviewStatus.trim() : '';
  if (!key)
    return { label: 'Not stated', tone: 'unknown', mark: '?', known: false };
  const known = REVIEW_DISPLAY[key];
  if (known) return { ...known, known: true };
  return {
    label: titleiseToken(key),
    tone: 'unknown',
    mark: '?',
    known: false,
  };
};

export const categoryLabel = (category) => {
  const key = typeof category === 'string' ? category.trim() : '';
  if (!key) return 'Uncategorised';
  return CATEGORY_LABELS[key] || titleiseToken(key);
};

/* -------------------------------------------------------------------------
 * FILE TYPE — WHAT THE BROWSER CAN HONESTLY SHOW
 * ---------------------------------------------------------------------- */

export const PREVIEW_KIND = {
  /** Rendered inline by the browser's own PDF viewer. */
  PDF: 'PDF',
  /** Rendered inline as a raster/vector image. */
  IMAGE: 'IMAGE',
  /** No preview. The file is NOT interpreted, and the UI must say so. */
  NONE: 'NONE',
};

const IMAGE_TYPES = new Set([
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'svg',
  'bmp',
]);
const CAD_TYPES = new Set(['dwg', 'dxf', 'dwf']);
const SHEET_TYPES = new Set(['xls', 'xlsx', 'csv']);
const SLIDE_TYPES = new Set(['ppt', 'pptx']);
const WORD_TYPES = new Set(['doc', 'docx']);
const ARCHIVE_TYPES = new Set(['zip', 'rar', '7z']);
const TEXT_TYPES = new Set(['txt']);

/** `PDF`, `.pdf`, `application/pdf`-ish tails and empty all normalise here. */
export const normaliseFileType = (fileType) => {
  if (typeof fileType !== 'string') return '';
  return fileType.trim().toLowerCase().replace(/^\./, '');
};

/** Extension from a filename, for rows whose `file_type` the backend omitted. */
export const fileTypeFromName = (name) => {
  if (typeof name !== 'string') return '';
  const dot = name.lastIndexOf('.');
  if (dot <= 0 || dot === name.length - 1) return '';
  return normaliseFileType(name.slice(dot + 1));
};

export const previewKindForFileType = (fileType) => {
  const ext = normaliseFileType(fileType);
  if (ext === 'pdf') return PREVIEW_KIND.PDF;
  if (IMAGE_TYPES.has(ext)) return PREVIEW_KIND.IMAGE;
  return PREVIEW_KIND.NONE;
};

/**
 * WHY there is no preview, in the words of what was actually not done.
 *
 * The CAD line is the one that matters most. OMNΛS has no DWG/DXF/DWF
 * renderer and no backend contract that interprets one, so the only honest
 * thing this surface can say about a CAD file is that it has not looked at
 * it. Anything softer reads as "we tried and it did not work", which invites
 * the belief that somewhere in here a drawing was understood.
 */
export const noPreviewReason = (fileType) => {
  const ext = normaliseFileType(fileType);
  if (CAD_TYPES.has(ext)) {
    return {
      headline: 'Preview not available for this file type',
      detail:
        'OMNΛS has no CAD renderer. This file has not been opened or interpreted — nothing on this screen is derived from its contents. Download it to open in your CAD application.',
    };
  }
  if (WORD_TYPES.has(ext) || SHEET_TYPES.has(ext) || SLIDE_TYPES.has(ext)) {
    return {
      headline: 'Preview not available for this file type',
      detail:
        'Office documents are not rendered here. The record and its metadata are shown below; download the file to open it in its own application.',
    };
  }
  if (ARCHIVE_TYPES.has(ext)) {
    return {
      headline: 'Preview not available for this file type',
      detail:
        'Archives are not opened here. OMNΛS has not inspected what is inside this file. Download it to extract it.',
    };
  }
  if (TEXT_TYPES.has(ext)) {
    return {
      headline: 'Preview not available for this file type',
      detail:
        'Inline text preview is not part of this surface. The record and its metadata are shown below; download the file to read it.',
    };
  }
  return {
    headline: 'Preview not available for this file type',
    detail: ext
      ? `OMNΛS does not render .${ext} files. The record and its metadata are shown below.`
      : 'This record does not name a file type, so no preview can be attempted.',
  };
};

/**
 * Icon CHOICE, not an icon. Keeping this a token means every classification
 * rule is testable in a plain Jest suite with no React and no lucide import.
 */
export const documentIconKey = (fileType) => {
  const ext = normaliseFileType(fileType);
  if (ext === 'pdf') return 'pdf';
  if (IMAGE_TYPES.has(ext)) return 'image';
  if (CAD_TYPES.has(ext)) return 'cad';
  if (SHEET_TYPES.has(ext)) return 'sheet';
  if (SLIDE_TYPES.has(ext)) return 'slides';
  if (WORD_TYPES.has(ext)) return 'doc';
  if (ARCHIVE_TYPES.has(ext)) return 'archive';
  if (TEXT_TYPES.has(ext)) return 'text';
  return 'file';
};

/**
 * `null`/absent is UNKNOWN and says so. Zero is a real measurement — the
 * backend types `file_size` as an int, so a genuinely empty file reads 0 B
 * and an absent one does not borrow that number.
 */
export const formatFileSize = (bytes) => {
  if (
    bytes === null ||
    bytes === undefined ||
    typeof bytes !== 'number' ||
    Number.isNaN(bytes)
  ) {
    return 'Unknown';
  }
  if (bytes < 0) return 'Unknown';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

/* -------------------------------------------------------------------------
 * THE FOUR REALITIES
 * ---------------------------------------------------------------------- */

export const AVAILABILITY = {
  /** Record + object + signed URL + a format this browser renders. */
  PREVIEW_READY: 'PREVIEW_READY',
  /** Record + object + signed URL. The file is reachable, not previewable. */
  FILE_READY: 'FILE_READY',
  /**
   * Record + a named stored object, but no signed URL in this response.
   * The document EXISTS. Only the temporary link is absent.
   */
  LINK_NOT_ISSUED: 'LINK_NOT_ISSUED',
  /** Record only: this row names no stored object at all. */
  NO_STORED_OBJECT: 'NO_STORED_OBJECT',
};

const AVAILABILITY_DISPLAY = {
  PREVIEW_READY: {
    label: 'Preview available',
    mark: '◉',
    tone: 'settled',
    meaning:
      'The record exists, the file is in storage, a temporary link was issued, and this format renders here.',
  },
  FILE_READY: {
    label: 'File available',
    mark: '↓',
    tone: 'neutral',
    meaning:
      'The record exists, the file is in storage and a temporary link was issued. This format is not rendered here.',
  },
  LINK_NOT_ISSUED: {
    label: 'Link not issued',
    mark: '⊗',
    tone: 'attention',
    meaning:
      'The record exists and names a stored file, but no temporary download link came back with this response. This is not a missing document.',
  },
  NO_STORED_OBJECT: {
    label: 'No stored file',
    mark: '∅',
    tone: 'unknown',
    meaning:
      'The record exists but names no stored object. Nothing has been uploaded against it.',
  },
};

export const availabilityDisplay = (key) =>
  AVAILABILITY_DISPLAY[key] || {
    label: 'Not stated',
    mark: '?',
    tone: 'unknown',
    meaning: 'The availability of this document has not been established.',
  };

const isNonEmptyString = (value) =>
  typeof value === 'string' && value.trim().length > 0;

/**
 * The four claims, decided separately and then combined — never inferred from
 * one another. `storageHoldsFile` reads `file_url` (what the row says about
 * the object store) and `signedUrlIssued` reads `download_url` (what this
 * response says about a temporary link); the backend mints the second from
 * the first and can fail to, which is precisely the case that must not read
 * as a missing document.
 */
export const documentAvailability = (doc) => {
  const recordExists = true; // reaching here means readDocumentContract accepted it
  const storageHoldsFile = isNonEmptyString(doc?.file_url);
  const signedUrlIssued = isNonEmptyString(doc?.download_url);
  const fileType =
    normaliseFileType(doc?.file_type) || fileTypeFromName(doc?.name);
  const previewKind = previewKindForFileType(fileType);
  const formatPreviewable = previewKind !== PREVIEW_KIND.NONE;

  let state;
  if (!storageHoldsFile) state = AVAILABILITY.NO_STORED_OBJECT;
  else if (!signedUrlIssued) state = AVAILABILITY.LINK_NOT_ISSUED;
  else if (formatPreviewable) state = AVAILABILITY.PREVIEW_READY;
  else state = AVAILABILITY.FILE_READY;

  return {
    recordExists,
    storageHoldsFile,
    signedUrlIssued,
    formatPreviewable,
    previewKind,
    state,
    display: availabilityDisplay(state),
  };
};

/* -------------------------------------------------------------------------
 * READING THE CONTRACT
 *
 * Optional chaining defends ONE field and does not validate a shape. These
 * functions decide the shape once, before anything reads it.
 * ---------------------------------------------------------------------- */

/**
 * @returns {{readable: true, document: object} | {readable: false, reason: string, id: string|null}}
 */
export const readDocumentContract = (raw) => {
  if (raw === null || raw === undefined) {
    return {
      readable: false,
      reason: 'No document payload was returned.',
      id: null,
    };
  }
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    return {
      readable: false,
      reason: 'The document payload is not an object.',
      id: null,
    };
  }
  if (!isNonEmptyString(raw.id)) {
    return {
      readable: false,
      reason: 'The document payload carries no id.',
      id: null,
    };
  }
  if (!isNonEmptyString(raw.name)) {
    // A nameless row cannot be listed, selected or spoken aloud. It is
    // reported as unreadable rather than given a name we invented.
    return {
      readable: false,
      reason: 'The document payload carries no name.',
      id: raw.id,
    };
  }

  const fileType =
    normaliseFileType(raw.file_type) || fileTypeFromName(raw.name);
  const availability = documentAvailability(raw);
  const status = statusDisplay(raw.status);
  const review = reviewStatusDisplay(raw.review_status);
  const version =
    Number.isInteger(raw.version) && raw.version >= 1 ? raw.version : null;

  return {
    readable: true,
    document: {
      id: raw.id,
      projectId: isNonEmptyString(raw.project_id) ? raw.project_id : null,
      name: raw.name,
      description: isNonEmptyString(raw.description) ? raw.description : null,
      category: isNonEmptyString(raw.category) ? raw.category.trim() : null,
      categoryLabel: categoryLabel(raw.category),
      fileType,
      fileTypeLabel: fileType ? `.${fileType}` : 'Not stated',
      iconKey: documentIconKey(fileType),
      fileSize: typeof raw.file_size === 'number' ? raw.file_size : null,
      fileSizeLabel: formatFileSize(
        typeof raw.file_size === 'number' ? raw.file_size : null,
      ),
      version,
      versionLabel: version === null ? 'Not stated' : `v${version}`,
      status: isNonEmptyString(raw.status) ? raw.status.trim() : null,
      statusDisplay: status,
      reviewStatus: isNonEmptyString(raw.review_status)
        ? raw.review_status.trim()
        : null,
      reviewStatusDisplay: review,
      uploadedById: isNonEmptyString(raw.uploaded_by_id)
        ? raw.uploaded_by_id
        : null,
      createdAt: isNonEmptyString(raw.created_at) ? raw.created_at : null,
      updatedAt: isNonEmptyString(raw.updated_at) ? raw.updated_at : null,
      /**
       * Backend-owned, temporary, and never reconstructed here. There is no
       * code path in this frontend that builds a bucket URL.
       */
      downloadUrl: isNonEmptyString(raw.download_url) ? raw.download_url : null,
      hasStoredObject: availability.storageHoldsFile,
      availability,
      previewKind: availability.previewKind,
    },
  };
};

/**
 * The list. An unreadable ROW does not destroy the list, and is not quietly
 * dropped either — it is counted, so "11 of 12 rows could be read" is
 * sayable. An unreadable ENVELOPE is refused whole, because a list whose
 * shape is unknown cannot be reported as empty without claiming this project
 * has no documents.
 */
export const readDocumentListContract = (raw) => {
  if (raw === null || raw === undefined) {
    return {
      readable: false,
      reason: 'No response was returned.',
      documents: [],
      unreadable: [],
    };
  }
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    return {
      readable: false,
      reason:
        'The document list payload is not an object. MALFORMED IS NOT EMPTY.',
      documents: [],
      unreadable: [],
    };
  }
  if (!Array.isArray(raw.documents)) {
    return {
      readable: false,
      reason:
        'The document list payload carries no `documents` array. MALFORMED IS NOT EMPTY.',
      documents: [],
      unreadable: [],
    };
  }

  const documents = [];
  const unreadable = [];
  const seen = new Set();
  for (const row of raw.documents) {
    const read = readDocumentContract(row);
    if (read.readable) {
      // A duplicate id would give two list rows the same React key and the
      // same selection target. Keep the first and report the collision.
      if (seen.has(read.document.id)) {
        unreadable.push({
          id: read.document.id,
          reason: 'A second row repeats this document id.',
        });
        continue;
      }
      seen.add(read.document.id);
      documents.push(read.document);
    } else {
      unreadable.push({ id: read.id, reason: read.reason });
    }
  }

  return {
    readable: true,
    reason: null,
    documents,
    unreadable,
    /** The backend's own count, kept separate from what could be read. */
    reportedTotal: typeof raw.total === 'number' ? raw.total : null,
  };
};

/* -------------------------------------------------------------------------
 * STORAGE REFUSALS — AN OPERATIONAL CONDITION IS NOT A FAILED UPLOAD
 * ---------------------------------------------------------------------- */

export const STORAGE_REFUSAL = {
  NOT_CONFIGURED: 'STORAGE_NOT_CONFIGURED',
};

/**
 * `503 document_storage_not_configured` is the deployment saying object
 * storage was never wired up for this environment. It is not "upload failed"
 * and it is emphatically not "document missing": nothing was attempted and
 * nothing was lost. The backend refuses before it reads the request body.
 *
 * Note the detail is a PLAIN STRING, not the `{code, message}` object that
 * `classifyWorkflowError` keys `disabled` on — so that classifier reports it
 * as `server` with the raw token as its message. Refining it here keeps the
 * shared classifier's behaviour for its ~100 other callers untouched.
 *
 * @param {object} classified output of classifyWorkflowError
 * @param {*} rawDetail the untouched `error.response.data.detail`
 */
export const readStorageRefusal = (classified, rawDetail) => {
  const token =
    typeof rawDetail === 'string' ? rawDetail.trim() : classified?.code || '';
  if (
    classified?.status === 503 &&
    token === 'document_storage_not_configured'
  ) {
    return {
      kind: STORAGE_REFUSAL.NOT_CONFIGURED,
      headline: 'Document storage is not configured for this environment.',
      detail:
        'The backend refused before reading the file. Nothing was uploaded, nothing was lost, and no existing document is affected. An administrator configures object storage in the deployment.',
      /** Operational condition, not a fault the user can retry away. */
      retryable: false,
    };
  }
  return null;
};

/**
 * The message an upload failure should show. Storage-not-configured is
 * answered in its own words; everything else keeps the classified message,
 * and an object `detail` never reaches a toast as `[object Object]`.
 */
export const presentUploadFailure = (classified, rawDetail) => {
  const refusal = readStorageRefusal(classified, rawDetail);
  if (refusal) return { ...refusal, classified };
  return {
    kind: 'UPLOAD_FAILED',
    headline: 'The upload did not complete.',
    detail: classified?.message || 'The server did not accept this file.',
    retryable: true,
    classified,
  };
};

/* -------------------------------------------------------------------------
 * SELECTION — WHICH ROW IS BEING LOOKED AT
 * ---------------------------------------------------------------------- */

/**
 * A selection survives a refresh only if the row is still there. Requesting
 * a document that has left the list resolves to nothing rather than holding a
 * stale id that would keep a deleted document's preview on screen.
 */
export const resolveSelection = (documents, requestedId) => {
  if (!Array.isArray(documents) || documents.length === 0) return null;
  if (!isNonEmptyString(requestedId)) return null;
  return documents.some((d) => d.id === requestedId) ? requestedId : null;
};

/**
 * WHERE FOCUS GOES WHEN THE SELECTED ROW IS DELETED.
 *
 * Focus that lands nowhere sends a keyboard or screen-reader user back to the
 * top of the document, which on this page means past the whole rail. The row
 * that took the deleted row's place is the nearest thing to "where you were":
 * the next one down, or the last row if the deleted one was last, or nothing
 * at all if the list is now empty.
 *
 * @param {Array} documentsBefore the list as it was, in display order
 * @param {string} deletedId
 * @returns {string|null} id that should receive selection and focus
 */
export const selectionAfterDeletion = (documentsBefore, deletedId) => {
  if (!Array.isArray(documentsBefore)) return null;
  const index = documentsBefore.findIndex((d) => d.id === deletedId);
  if (index === -1) return null;
  const remaining = documentsBefore.filter((d) => d.id !== deletedId);
  if (remaining.length === 0) return null;
  return remaining[Math.min(index, remaining.length - 1)].id;
};

/**
 * Keyboard movement within the document rail. Returned as an INDEX so the
 * component owns focus and this stays testable without a DOM.
 * Home/End are included because a 200-drawing rail is unusable without them.
 */
export const nextIndexForKey = (key, currentIndex, length) => {
  if (!Number.isInteger(length) || length <= 0) return null;
  const at =
    Number.isInteger(currentIndex) && currentIndex >= 0
      ? Math.min(currentIndex, length - 1)
      : 0;
  switch (key) {
    case 'ArrowDown':
      return Math.min(at + 1, length - 1);
    case 'ArrowUp':
      return Math.max(at - 1, 0);
    case 'Home':
      return 0;
    case 'End':
      return length - 1;
    default:
      return null;
  }
};

/* -------------------------------------------------------------------------
 * BROWSING
 * ---------------------------------------------------------------------- */

/**
 * The rail's buckets. Built from what the backend actually sent, in the
 * canonical order, and any category the backend returns that this frontend
 * does not know is appended under its own name rather than dropped — dropping
 * it would hide documents from the only surface that lists them.
 */
export const categoryBuckets = (documents) => {
  const rows = Array.isArray(documents) ? documents : [];
  const counts = new Map();
  for (const doc of rows) {
    const key = isNonEmptyString(doc?.category)
      ? doc.category.trim()
      : '__uncategorised__';
    counts.set(key, (counts.get(key) || 0) + 1);
  }

  const known = DOCUMENT_CATEGORIES.filter((c) => counts.has(c.value)).map(
    (c) => ({
      value: c.value,
      label: c.label,
      count: counts.get(c.value),
    }),
  );
  const knownValues = new Set(DOCUMENT_CATEGORIES.map((c) => c.value));
  const unknown = [...counts.entries()]
    .filter(
      ([value]) => value !== '__uncategorised__' && !knownValues.has(value),
    )
    .map(([value, count]) => ({ value, label: titleiseToken(value), count }));
  const uncategorised = counts.has('__uncategorised__')
    ? [
        {
          value: '__uncategorised__',
          label: 'Uncategorised',
          count: counts.get('__uncategorised__'),
        },
      ]
    : [];

  return [
    { value: 'all', label: 'All documents', count: rows.length },
    ...known,
    ...unknown,
    ...uncategorised,
  ];
};

/**
 * Search matches the fields a person can see: name, description, category
 * label and file type. It is a TEXT match over the record's own words and
 * never a claim about the file's contents — nothing here has read inside a
 * PDF or a drawing.
 */
export const filterDocuments = (
  documents,
  { query = '', category = 'all' } = {},
) => {
  const rows = Array.isArray(documents) ? documents : [];
  const needle = typeof query === 'string' ? query.trim().toLowerCase() : '';
  return rows.filter((doc) => {
    const matchesCategory =
      category === 'all' ||
      (category === '__uncategorised__'
        ? !isNonEmptyString(doc?.category)
        : doc?.category === category);
    if (!matchesCategory) return false;
    if (!needle) return true;
    const haystack = [
      doc?.name,
      doc?.description,
      doc?.categoryLabel,
      doc?.fileType,
    ]
      .filter(isNonEmptyString)
      .join(' ')
      .toLowerCase();
    return haystack.includes(needle);
  });
};

/** ISO timestamp → a readable local string, or an honest unknown. */
export const formatTimestamp = (iso) => {
  if (!isNonEmptyString(iso)) return 'Not stated';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return 'Not stated';
  return parsed.toLocaleString();
};

export default {
  DOCUMENT_CONTRACT,
  readDocumentContract,
  readDocumentListContract,
  documentAvailability,
  previewKindForFileType,
  noPreviewReason,
  readStorageRefusal,
  presentUploadFailure,
  resolveSelection,
  selectionAfterDeletion,
  nextIndexForKey,
  categoryBuckets,
  filterDocuments,
};
